import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import useXHR from '../../hooks/useXHR';
import { useAuth } from '../../contexts/AuthContext';

const COLORS = ['#FDE047', '#FDE68A', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#FCD34D', '#14b8a6'];

const DarkTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: 'rgba(10,5,20,0.95)', border: '1px solid rgba(253, 224, 71,0.2)', borderRadius: 8, padding: '6px 10px', fontSize: '0.6rem' }}>
      <div style={{ color: '#334155', fontWeight: 600, marginBottom: 2 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color || '#047857' }}>{p.name}: {typeof p.value === 'number' ? p.value.toFixed(1) : p.value}</div>
      ))}
    </div>
  );
};

const StatCard = ({ label, value, sub, color = '#047857' }) => (
  <div style={{
    background: `${color}08`, border: `1px solid ${color}18`, borderRadius: 8,
    padding: '8px 12px', textAlign: 'center', flex: 1, minWidth: 80
  }}>
    <div style={{ color, fontSize: '1.2rem', fontWeight: 800, lineHeight: 1 }}>{value}</div>
    <div style={{ color: '#888', fontSize: '0.52rem', fontWeight: 600, marginTop: 2 }}>{label}</div>
    {sub && <div style={{ color: '#555', fontSize: '0.45rem', marginTop: 1 }}>{sub}</div>}
  </div>
);

/**
 * ProtocolPerformanceAnalytics - Stats dashboard for a single protocol/category.
 * Shows match rate, quality scores, growth timeline, top domains, doc types.
 */
const ProtocolPerformanceAnalytics = ({ categoryId, categoryName, onClose, showToast }) => {
  const { token } = useAuth();
  const xhr = useXHR(token);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const loadAnalytics = useCallback((d) => {
    if (!token || !categoryId) return;
    setLoading(true);
    xhr.get(`/protocol-analytics/${categoryId}?days=${d || days}`, {
      onSuccess: (res) => setData(res),
      onError: (err) => showToast && showToast(err || 'Failed to load analytics', 'error'),
      onFinally: () => setLoading(false)
    });
  }, [xhr, token, categoryId, days, showToast]);

  useEffect(() => { loadAnalytics(); }, [loadAnalytics]);

  const handleDaysChange = (d) => { setDays(d); loadAnalytics(d); };

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: '#888', fontSize: '0.75rem' }}>
        <div style={{ marginBottom: 8 }}>Loading protocol analytics...</div>
        <div style={{ color: '#555', fontSize: '0.55rem' }}>Crunching numbers for "{categoryName}"</div>
      </div>
    );
  }

  if (!data) return null;

  const ov = data.overview || {};
  const ms = data.match_scores || {};
  const q = data.quality || {};

  return (
    <div data-testid="protocol-performance-analytics" style={{
      background: 'linear-gradient(135deg, rgba(20,10,40,0.98), rgba(30,15,50,0.95))',
      borderRadius: 12, border: '1px solid rgba(253, 224, 71,0.15)', overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '10px 14px', background: 'rgba(253, 224, 71,0.08)', borderBottom: '1px solid rgba(253, 224, 71,0.1)'
      }}>
        <div>
          <div style={{ color: '#c4b5fd', fontSize: '0.85rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
            <i className="fas fa-chart-line" style={{ fontSize: '0.75rem' }} />
            Protocol Analytics: {categoryName}
          </div>
          <div style={{ color: '#666', fontSize: '0.5rem', marginTop: 1, fontFamily: 'monospace' }}>
            {data.category?.protocol || 'No protocol'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          {[7, 30, 90].map(d => (
            <button key={d} onClick={() => handleDaysChange(d)} data-testid={`proto-days-${d}`}
              style={{
                background: days === d ? 'rgba(253, 224, 71,0.2)' : 'transparent',
                border: `1px solid ${days === d ? 'rgba(253, 224, 71,0.3)' : 'rgba(255,255,255,0.06)'}`,
                color: days === d ? '#047857' : '#666', padding: '3px 10px', borderRadius: 6,
                fontSize: '0.6rem', fontWeight: days === d ? 700 : 400, cursor: 'pointer'
              }}
            >{d}d</button>
          ))}
          {onClose && (
            <button onClick={onClose} style={{
              background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.25)',
              color: '#f87171', width: 22, height: 22, borderRadius: 5, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700
            }} data-testid="close-protocol-analytics">X</button>
          )}
        </div>
      </div>

      {/* Stats Row */}
      <div style={{ display: 'flex', gap: 6, padding: '10px 12px', flexWrap: 'wrap' }}>
        <StatCard label="Total URLs" value={ov.total_results || 0} color="#FDE047" />
        <StatCard label={`Last ${days}d`} value={ov.recent_results || 0} sub={`${ov.growth_rate || 0}/day avg`} color="#06b6d4" />
        <StatCard label="Avg Match" value={`${ms.avg_score || 0}%`} sub={`${ms.min_score || 0}%-${ms.max_score || 0}%`} color="#10b981" />
        <StatCard label="Avg Quality" value={`${q.avg_quality || 0}`} sub={`${q.high_quality_rate || 0}% high quality`} color="#f59e0b" />
        <StatCard label="EI Tagged" value={ov.ei_tagged || 0} color="#FDE68A" />
      </div>

      {/* Charts */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, padding: '0 12px 10px' }}>
        {/* Growth Timeline */}
        <div style={{ background: 'rgba(255, 255, 255, 0.85)', borderRadius: 8, padding: 8, border: '1px solid rgba(255, 255, 255, 0.85)' }}>
          <div style={{ color: '#888', fontSize: '0.55rem', fontWeight: 600, marginBottom: 6 }}>Category Growth</div>
          {data.growth_timeline?.length > 0 ? (
            <ResponsiveContainer width="100%" height={140}>
              <AreaChart data={data.growth_timeline} margin={{ top: 2, right: 4, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.85)" />
                <XAxis dataKey="date" tick={{ fill: '#555', fontSize: 8 }} axisLine={false} tickLine={false} tickFormatter={(v) => v.slice(5)} />
                <YAxis tick={{ fill: '#555', fontSize: 8 }} axisLine={false} tickLine={false} />
                <Tooltip content={<DarkTooltip />} />
                <Area type="monotone" dataKey="count" stroke="#FDE047" fill="rgba(253, 224, 71,0.15)" strokeWidth={2} name="URLs Added" />
                <Area type="monotone" dataKey="avg_score" stroke="#10b981" fill="rgba(16,185,129,0.08)" strokeWidth={1} name="Avg Match %" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#555', fontSize: '0.6rem' }}>No growth data yet</div>
          )}
        </div>

        {/* Top Domains */}
        <div style={{ background: 'rgba(255, 255, 255, 0.85)', borderRadius: 8, padding: 8, border: '1px solid rgba(255, 255, 255, 0.85)' }}>
          <div style={{ color: '#888', fontSize: '0.55rem', fontWeight: 600, marginBottom: 6 }}>Top Domains</div>
          {data.top_domains?.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3, maxHeight: 140, overflowY: 'auto' }}>
              {data.top_domains.map((d, i) => {
                const maxCount = data.top_domains[0]?.count || 1;
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ color: '#888', fontSize: '0.5rem', minWidth: 14, textAlign: 'right' }}>{i + 1}.</span>
                    <div style={{ flex: 1, position: 'relative', height: 16, borderRadius: 4, overflow: 'hidden', background: 'rgba(255, 255, 255, 0.85)' }}>
                      <div style={{
                        position: 'absolute', left: 0, top: 0, bottom: 0,
                        width: `${(d.count / maxCount) * 100}%`,
                        background: `${COLORS[i % COLORS.length]}25`,
                        borderRadius: 4
                      }} />
                      <span style={{ position: 'relative', zIndex: 1, color: '#ccc', fontSize: '0.5rem', padding: '0 4px', lineHeight: '16px' }}>
                        {d.domain}
                      </span>
                    </div>
                    <span style={{ color: '#666', fontSize: '0.48rem', minWidth: 25, textAlign: 'right' }}>{d.count}</span>
                    <span style={{ color: '#555', fontSize: '0.42rem', minWidth: 30, textAlign: 'right' }}>{d.avg_score}%</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#555', fontSize: '0.6rem' }}>No domain data</div>
          )}
        </div>

        {/* Document Types */}
        <div style={{ background: 'rgba(255, 255, 255, 0.85)', borderRadius: 8, padding: 8, border: '1px solid rgba(255, 255, 255, 0.85)' }}>
          <div style={{ color: '#888', fontSize: '0.55rem', fontWeight: 600, marginBottom: 6 }}>Document Types</div>
          {data.document_types?.length > 0 ? (
            <ResponsiveContainer width="100%" height={140}>
              <PieChart>
                <Pie data={data.document_types} cx="50%" cy="50%" innerRadius={35} outerRadius={60} paddingAngle={2} dataKey="count" nameKey="type">
                  {data.document_types.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} fillOpacity={0.7} />
                  ))}
                </Pie>
                <Tooltip content={<DarkTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#555', fontSize: '0.6rem' }}>No type data</div>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginTop: 2 }}>
            {(data.document_types || []).slice(0, 6).map((dt, i) => (
              <span key={i} style={{ background: `${COLORS[i % COLORS.length]}15`, color: COLORS[i % COLORS.length], padding: '1px 5px', borderRadius: 6, fontSize: '0.42rem' }}>
                {dt.type?.substring(0, 14)} ({dt.count})
              </span>
            ))}
          </div>
        </div>

        {/* Match Score Breakdown */}
        <div style={{ background: 'rgba(255, 255, 255, 0.85)', borderRadius: 8, padding: 8, border: '1px solid rgba(255, 255, 255, 0.85)' }}>
          <div style={{ color: '#888', fontSize: '0.55rem', fontWeight: 600, marginBottom: 6 }}>Performance Summary</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '4px 0' }}>
            {/* Match Score Bar */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                <span style={{ color: '#999', fontSize: '0.48rem' }}>Match Score</span>
                <span style={{ color: '#10b981', fontSize: '0.5rem', fontWeight: 700 }}>{ms.avg_score || 0}%</span>
              </div>
              <div style={{ height: 8, background: 'rgba(255, 255, 255, 0.85)', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${ms.avg_score || 0}%`, background: 'linear-gradient(90deg, #10b981, #34d399)', borderRadius: 4 }} />
              </div>
            </div>
            {/* Quality Score Bar */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                <span style={{ color: '#999', fontSize: '0.48rem' }}>Quality Score</span>
                <span style={{ color: '#f59e0b', fontSize: '0.5rem', fontWeight: 700 }}>{q.avg_quality || 0}</span>
              </div>
              <div style={{ height: 8, background: 'rgba(255, 255, 255, 0.85)', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${Math.min(100, q.avg_quality || 0)}%`, background: 'linear-gradient(90deg, #f59e0b, #fbbf24)', borderRadius: 4 }} />
              </div>
            </div>
            {/* High Quality Rate */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                <span style={{ color: '#999', fontSize: '0.48rem' }}>High Quality Rate</span>
                <span style={{ color: '#FDE047', fontSize: '0.5rem', fontWeight: 700 }}>{q.high_quality_rate || 0}%</span>
              </div>
              <div style={{ height: 8, background: 'rgba(255, 255, 255, 0.85)', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${q.high_quality_rate || 0}%`, background: 'linear-gradient(90deg, #FDE047, #047857)', borderRadius: 4 }} />
              </div>
            </div>
            {/* EI Rate */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                <span style={{ color: '#999', fontSize: '0.48rem' }}>EI Tagged Rate</span>
                <span style={{ color: '#047857', fontSize: '0.5rem', fontWeight: 700 }}>
                  {ov.total_results > 0 ? ((ov.ei_tagged / ov.total_results) * 100).toFixed(1) : 0}%
                </span>
              </div>
              <div style={{ height: 8, background: 'rgba(255, 255, 255, 0.85)', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${ov.total_results > 0 ? (ov.ei_tagged / ov.total_results) * 100 : 0}%`, background: 'linear-gradient(90deg, #FDE68A, #FEF08A)', borderRadius: 4 }} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Microfont */}
      <div style={{ padding: '2px 12px 6px', color: '#3a3a3a', fontSize: '0.42rem', textAlign: 'center' }}>
        Protocol performance over {days} days. Match Score = how well URLs match protocol keywords. Quality = content richness. EI = Educated Individual Site tagged content.
      </div>
    </div>
  );
};

export default ProtocolPerformanceAnalytics;
